import styled from 'styled-components';
import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import {
  getCareerConfig,
  submitCareerApplication,
} from '../../api/services/careerService';

const SectionContainer = styled.section`
  margin: 0 auto;
  padding: 40px 20px;
  width: 100%;
  max-width: 1000px;
`;

const Title = styled.h1`
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 13px;
  color: #888;
  margin-top: 8px;
  margin-bottom: 40px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FieldWrapper = styled.div`
  ${(props) => props.$fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  color: #e03131;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const PositionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PositionOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${(props) => (props.$active ? '#000' : '#e5e5e5')};
  background-color: ${(props) => (props.$active ? '#fafafa' : '#fff')};
  border-radius: 10px;
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    border-color: #999;
  }
`;

const RadioInput = styled.input`
  accent-color: #000;
`;

const QuestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const QuestionBlock = styled.div``;

const QuestionLabel = styled.p`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const YesNoWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

const YesNoOption = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  cursor: pointer;
`;

const EducationEntry = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 16px;
  border: 1px solid #eee;
  border-radius: 10px;
  position: relative;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ReferenceEntry = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 16px;
  border: 1px solid #eee;
  border-radius: 10px;
  position: relative;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  background: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const AddButton = styled.button`
  align-self: flex-start;
  border: 1px dashed #999;
  border-radius: 8px;
  background: #fff;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    border-color: #333;
  }
`;

const RemoveButton = styled.button`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fff;
  color: #666;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    border-color: #e03131;
    color: #e03131;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const EducationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubmitButton = styled.button`
  align-self: center;
  border: none;
  border-radius: 8px;
  background: #000;
  color: #fff;
  padding: 12px 40px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingText = styled.p`
  text-align: center;
  color: #888;
  font-size: 14px;
`;

const ClosedText = styled.p`
  text-align: center;
  color: #666;
  font-size: 14px;
  line-height: 1.6;
`;

const BASIC_QUESTIONS = [
  {
    key: 'basicQ1',
    label: "Do you have a valid driver's license?",
  },
  {
    key: 'basicQ2',
    label: 'Are you able to travel nationwide if required?',
  },
  {
    key: 'basicQ3',
    label: 'Valid U.S. work authorization',
  },
];

export default function CareerApplicationForm() {
  const [careerConfig, setCareerConfig] = useState({
    positions: [],
    questions: [],
    postStatus: 1,
    loading: true,
  });

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    position: '',
    education: [
      {
        id: 1,
        name: '',
        type: '',
        address: '',
        yearGraduated: '',
        major: '',
      },
    ],
    workExperience: [
      {
        id: 1,
        employer: '',
        address: '',
        telephone: '',
        jobTitle: '',
        supervisor: '',
        startDate: '',
        endDate: '',
        reasonForLeaving: '',
        duties: '',
      },
    ],
    references: [
      {
        id: 1,
        name: '',
        phone: '',
        relationship: '',
      },
      {
        id: 2,
        name: '',
        phone: '',
        relationship: '',
      },
      {
        id: 3,
        name: '',
        phone: '',
        relationship: '',
      },
    ],
    extraQuestions: {
      q1: '',
      q2: '',
      q3: '',
      q4: '',
      q5: '',
      q6: '',
      q7: '',
      q8: '',
      q9: '',
      q10: '',
    },
    resumeFile: null,
    portfolioFile: null,
  });

  const [basicAnswers, setBasicAnswers] = useState({});

  const EDUCATION_TYPES = [
    'High School',
    'College',
    'Graduate School',
    'Specialized Training School',
    'Other Education',
  ];

  useEffect(() => {
    const loadCareerConfig = async () => {
      try {
        const result = await getCareerConfig();

        console.log('CAREER CONFIG API:', result);
        console.log('POST STATUS:', result?.postStatus);
        setCareerConfig({
          positions: result?.positions || [],
          questions: result?.questions || [],
          postStatus: result?.postStatus ?? 1,
          loading: false,
        });
      } catch (error) {
        setCareerConfig({
          positions: [],
          questions: [],
          postStatus: 1,
          loading: false,
        });
      }
    };

    loadCareerConfig();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEducationChange = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      education: prev.education.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addEducationEntry = () => {
    setForm((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        {
          id: Date.now(),
          name: '',
          type: '',
          address: '',
          yearGraduated: '',
          major: '',
        },
      ],
    }));
  };

  const removeEducationEntry = (id) => {
    setForm((prev) => ({
      ...prev,
      education: prev.education.filter((item) => item.id !== id),
    }));
  };

  const handleWorkExperienceChange = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      workExperience: prev.workExperience.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addWorkExperienceEntry = () => {
    setForm((prev) => ({
      ...prev,
      workExperience: [
        ...prev.workExperience,
        {
          id: Date.now(),
          employer: '',
          address: '',
          telephone: '',
          jobTitle: '',
          supervisor: '',
          startDate: '',
          endDate: '',
          reasonForLeaving: '',
          duties: '',
        },
      ],
    }));
  };

  const removeWorkExperienceEntry = (id) => {
    setForm((prev) => ({
      ...prev,
      workExperience: prev.workExperience.filter((item) => item.id !== id),
    }));
  };

  const handleReferenceChange = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      references: prev.references.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const handleExtraQuestionChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      extraQuestions: {
        ...prev.extraQuestions,
        [key]: value,
      },
    }));
  };

  const handleFileChange = (field, file) => {
    setForm((prev) => ({
      ...prev,
      [field]: file,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();

      formData.append('firstName', form.firstName || '');
      formData.append('lastName', form.lastName || '');
      formData.append('email', form.email || '');
      formData.append('phone', form.phone || '');
      formData.append('address', form.address || '');
      formData.append('applyingPosition', form.position || '');

      formData.append('basic_q1', basicAnswers.basicQ1 === 'Yes' ? 'Y' : 'N');
      formData.append('basic_q2', basicAnswers.basicQ2 === 'Yes' ? 'Y' : 'N');
      formData.append('basic_q3', basicAnswers.basicQ3 === 'Yes' ? 'Y' : 'N');

      Object.entries(form.extraQuestions).forEach(([key, value]) => {
        formData.append(key, value || '');
      });

      formData.append('education', JSON.stringify(form.education));
      formData.append('workExperience', JSON.stringify(form.workExperience));
      formData.append('references', JSON.stringify(form.references));

      if (form.resumeFile) {
        formData.append('resumeFile', form.resumeFile);
      }

      if (form.portfolioFile) {
        formData.append('portfolioFile', form.portfolioFile);
      }

      const result = await submitCareerApplication(formData);

      if (!result?.is_success) {
        throw new Error(result?.message || 'Failed to submit application.');
      }

      alert('Application submitted successfully.');
    } catch (error) {
      console.error('Submit error:', error);
      console.error('Response:', error.response);
      console.error('Response data:', error.response?.data);
      console.error('Status:', error.response?.status);

      alert(
        error.response?.data?.message ||
          error.message ||
          'Failed to submit application.',
      );
    }
  };

  if (careerConfig.loading) {
    return (
      <Layout>
        <SectionContainer>
          <Title>JOB APPLICATION FORM</Title>
          <LoadingText>Loading application form...</LoadingText>
        </SectionContainer>
      </Layout>
    );
  }

  if (careerConfig.loading) {
    return (
      <Layout>
        <SectionContainer>
          <Title>JOB APPLICATION FORM</Title>
          <LoadingText>Loading application form...</LoadingText>
        </SectionContainer>
      </Layout>
    );
  }

  if (Number(careerConfig.postStatus) === 0) {
    return (
      <Layout>
        <SectionContainer>
          <Title>JOB APPLICATION</Title>
          <ClosedText>
            Applications are currently closed.
            <br />
            Please check back later.
          </ClosedText>
        </SectionContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <SectionContainer>
        <Title>JOB APPLICATION FORM</Title>

        <Subtitle>
          Complete the form below to request a personalized order.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          {/* Basic Information */}

          <InputGrid>
            <FieldWrapper>
              <Label>
                Your Name <RequiredMark>*</RequiredMark>
              </Label>

              <Input
                type='text'
                required
                value={form.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label>
                Last Name <RequiredMark>*</RequiredMark>
              </Label>

              <Input
                type='text'
                required
                value={form.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label>
                Email <RequiredMark>*</RequiredMark>
              </Label>

              <Input
                type='email'
                required
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label>
                Phone <RequiredMark>*</RequiredMark>
              </Label>

              <Input
                type='tel'
                required
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
            </FieldWrapper>

            <FieldWrapper $fullWidth>
              <Label>
                Current Address <RequiredMark>*</RequiredMark>
              </Label>

              <Input
                type='text'
                required
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
              />
            </FieldWrapper>
          </InputGrid>

          {/* Position Applying For */}

          <div>
            <Label>
              Position Applying For <RequiredMark>*</RequiredMark>
            </Label>

            <PositionGrid>
              {careerConfig.positions.map((pos) => (
                <PositionOption key={pos.id} $active={form.position === pos.id}>
                  <RadioInput
                    type='radio'
                    name='position'
                    value={pos.id}
                    required
                    checked={form.position === pos.id}
                    onChange={(e) =>
                      handleChange('position', Number(e.target.value))
                    }
                  />

                  {pos.label}
                </PositionOption>
              ))}
            </PositionGrid>
          </div>

          {/* Basic Questions */}

          {BASIC_QUESTIONS.length > 0 && (
            <QuestionGrid>
              {BASIC_QUESTIONS.map((q) => (
                <QuestionBlock key={q.key}>
                  <QuestionLabel>
                    {q.label} <RequiredMark>*</RequiredMark>
                  </QuestionLabel>

                  <YesNoWrapper>
                    {['Yes', 'No'].map((opt) => (
                      <YesNoOption key={opt}>
                        <RadioInput
                          type='radio'
                          name={q.key}
                          value={opt}
                          required
                          checked={basicAnswers[q.key] === opt}
                          onChange={(e) => {
                            setBasicAnswers((prev) => ({
                              ...prev,
                              [q.key]: e.target.value,
                            }));
                          }}
                        />

                        {opt}
                      </YesNoOption>
                    ))}
                  </YesNoWrapper>
                </QuestionBlock>
              ))}
            </QuestionGrid>
          )}

          {/* Education Background */}

          <div>
            <Label>Education Background</Label>

            <EducationList>
              {form.education.map((item) => (
                <EducationEntry key={item.id}>
                  <FieldWrapper $fullWidth>
                    <Select
                      value={item.type}
                      onChange={(e) =>
                        handleEducationChange(item.id, 'type', e.target.value)
                      }
                    >
                      <option value=''>Select Education Type</option>

                      {EDUCATION_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Select>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>Name</Label>

                    <Input
                      type='text'
                      value={item.name}
                      onChange={(e) =>
                        handleEducationChange(item.id, 'name', e.target.value)
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>Address</Label>

                    <Input
                      type='text'
                      value={item.address}
                      onChange={(e) =>
                        handleEducationChange(
                          item.id,
                          'address',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>Graduation Date</Label>

                    <Input
                      type='date'
                      value={item.yearGraduated || ''}
                      onChange={(e) =>
                        handleEducationChange(
                          item.id,
                          'yearGraduated',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  {item.type !== 'High School' && (
                    <FieldWrapper>
                      <Label>Major</Label>

                      <Input
                        type='text'
                        value={item.major}
                        onChange={(e) =>
                          handleEducationChange(
                            item.id,
                            'major',
                            e.target.value,
                          )
                        }
                      />
                    </FieldWrapper>
                  )}
                </EducationEntry>
              ))}

              <ActionRow>
                <AddButton type='button' onClick={addEducationEntry}>
                  + Add Education
                </AddButton>

                <RemoveButton
                  type='button'
                  disabled={form.education.length <= 1}
                  onClick={() =>
                    removeEducationEntry(
                      form.education[form.education.length - 1].id,
                    )
                  }
                >
                  Remove
                </RemoveButton>
              </ActionRow>
            </EducationList>
          </div>

          {/* Work Experience */}

          <div>
            <Label>Work Experience</Label>

            <EducationList>
              {form.workExperience.map((item) => (
                <EducationEntry key={item.id}>
                  <FieldWrapper>
                    <Label>Employer / Company Name</Label>

                    <Input
                      type='text'
                      value={item.employer}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'employer',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>Job Title</Label>

                    <Input
                      type='text'
                      value={item.jobTitle}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'jobTitle',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper $fullWidth>
                    <Label>Address</Label>

                    <Input
                      type='text'
                      value={item.address}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'address',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>Supervisor Name</Label>

                    <Input
                      type='text'
                      value={item.supervisor}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'supervisor',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>Telephone</Label>

                    <Input
                      type='tel'
                      value={item.telephone}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'telephone',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>Date Employed - Start</Label>

                    <Input
                      type='month'
                      value={item.startDate}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'startDate',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>Date Employed - End</Label>

                    <Input
                      type='month'
                      value={item.endDate}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'endDate',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper $fullWidth>
                    <Label>Reason for Leaving</Label>

                    <Input
                      type='text'
                      value={item.reasonForLeaving}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'reasonForLeaving',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper $fullWidth>
                    <Label>List All Job Duties Performed</Label>

                    <TextArea
                      value={item.duties}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          item.id,
                          'duties',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>
                </EducationEntry>
              ))}

              <ActionRow>
                <AddButton type='button' onClick={addWorkExperienceEntry}>
                  + Add Work Experience
                </AddButton>

                <RemoveButton
                  type='button'
                  disabled={form.workExperience.length <= 1}
                  onClick={() =>
                    removeWorkExperienceEntry(
                      form.workExperience[form.workExperience.length - 1].id,
                    )
                  }
                >
                  Remove
                </RemoveButton>
              </ActionRow>
            </EducationList>
          </div>

          {/* Personal References */}

          <div>
            <Label>Personal Referals</Label>

            <EducationList>
              {form.references.map((ref, idx) => (
                <ReferenceEntry key={ref.id}>
                  <FieldWrapper>
                    <Label>
                      Reference {idx + 1} (Do not list family)
                      <br />
                      <br />
                      Name
                    </Label>

                    <Input
                      type='text'
                      value={ref.name}
                      onChange={(e) =>
                        handleReferenceChange(ref.id, 'name', e.target.value)
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>
                      <br />
                      <br />
                      Phone #
                    </Label>

                    <Input
                      type='tel'
                      value={ref.phone}
                      onChange={(e) =>
                        handleReferenceChange(ref.id, 'phone', e.target.value)
                      }
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label>
                      <br />
                      <br />
                      Relationship
                    </Label>

                    <Input
                      type='text'
                      value={ref.relationship}
                      onChange={(e) =>
                        handleReferenceChange(
                          ref.id,
                          'relationship',
                          e.target.value,
                        )
                      }
                    />
                  </FieldWrapper>
                </ReferenceEntry>
              ))}
            </EducationList>
          </div>

          {/* Extra Questions */}

          <div>
            <Label>
              Questions
              <br />
              <br />
            </Label>

            <EducationList>
              {careerConfig.questions.map((question, idx) => {
                const key = `q${idx + 1}`;

                return (
                  <FieldWrapper key={key}>
                    <Label>{question.label}</Label>

                    <Input
                      type='text'
                      value={form.extraQuestions[key] || ''}
                      onChange={(e) =>
                        handleExtraQuestionChange(key, e.target.value)
                      }
                    />
                  </FieldWrapper>
                );
              })}
            </EducationList>
          </div>

          {/* Attachments */}

          <div>
            <Label>
              Attach your resume and/or portfolio
              <br />
            </Label>

            <InputGrid>
              <FieldWrapper>
                <Label>Resume</Label>

                <Input
                  type='file'
                  onChange={(e) =>
                    handleFileChange('resumeFile', e.target.files?.[0] || null)
                  }
                />
              </FieldWrapper>

              <FieldWrapper>
                <Label>Portfolio</Label>

                <Input
                  type='file'
                  onChange={(e) =>
                    handleFileChange(
                      'portfolioFile',
                      e.target.files?.[0] || null,
                    )
                  }
                />
              </FieldWrapper>
            </InputGrid>
          </div>

          {/* Submit */}

          <SubmitButton
            type='submit'
            disabled={careerConfig.positions.length === 0}
          >
            Submit
          </SubmitButton>
        </Form>
      </SectionContainer>
    </Layout>
  );
}
